import React, { useState, useEffect } from "react";
import classnames from "classnames";
import sortBy from "lodash.sortby";
import { View } from "@instructure/ui-view";
import { useStateValue } from "../../context-store/AppStateProvider";
import useCreateCourse from "../../hooks/useCreateCourse";
import { ACTIONS } from "../../context-store/ACTIONS";
import { PROGRESS_ALERT_VARIANTS } from "../ProgressAlerts/ProgressAlerts";
import styles from "../FormCreateCourseRestart/FormCreateCourseRestart.module.scss";
import { Grid } from "@instructure/ui-grid";
import { zonedTimeToUtc } from "date-fns-tz";
import { TimeZonesOptions } from "../../utils/timezonesOptions";
import timezoneMapping from "../../utils/tz.json";
import IasFormTimeInput from "../FormTimeInput/FormTimeInput";
import IasFormDateInputRestart from "../FormDateInput/FormDateInputRestart";
import IasFormSelectInputRestart from "../FormSelectRestart/FormSelectInputRestart";
import IasFormActions from "../FormActions/FormActionsRestart";
import {Text} from "@instructure/ui-text";
import IasFormTextInput from "../FormTextInput/FormTextInput";
import IasFormDateInput from "../FormDateInput/FormDateInput";

const IasFormCreateCourseRestart = ({
                                 dropdownOptions,
                                 dropdownDataError,
                                 userTimeZone,
                             }) => {
    const [{ maxDuration }, dispatch] = useStateValue();
    const [curriculumOptions, setCurriculumOptions] = useState([]);
    const [languageOptions, setLanguageOptions] = useState([]);
    const [deliveryModalityOptions, setDeliveryModalityOptions] = useState([]);
    const [countryOfDeliveryOptions, setCountryOfDeliveryOptions] = useState([]);
    const [timeZoneDropdownValue, setTimeZoneDropdownValue] = useState("");
    const [apiLoading, setApiLoading] = useState(true);
    const [resetForm, setResetForm] = useState(false);
    const [resetSubDropdowns, setResetSubDropdowns] = useState(false);
    const timeZoneOptions = TimeZonesOptions;

    const initialFormValues = {
        curriculum: null,
        startDate: null,
        start_at: "08:00 AM",
        endDate: null,
        end_at: "06:00 PM",
        language: null,
        timezone: userTimeZone ? userTimeZone : "",
        deliveryModality: null,
        countryOfDeliveries: null,
        correctST: null,
        correctET: null,
        cohort1: "",
        cohort2: "",
        cohort3: "",
        cohort4: "",
        cohort5: "",
        cohort6: "",
        cohorts: null,
    };

    const initialFormValidation = {
        curriculum: false,
        startDate: false,
        endDate: false,
        language: false,
        deliveryModality: false,
        countryOfDeliveries: false,
        cohort1: false,
        cohort2: true,
        cohort3: true,
        cohort4: true,
        cohort5: true,
        cohort6: true,
    };

    const [formValues, setFormValues] = useState(initialFormValues);
    const [formValidation, setFormValidation] = useState(initialFormValidation);
    const [isFormValid, setIsFormValid] = useState(false);
    const [isFormDirty, setIsFormDirty] = useState(false);

    // API Calls here to build out dropdowns or classes
    useEffect(() => {
        // only curriculum has masterCurriculumNumber
        let curricula = dropdownOptions.map((o) => {
            return {
                id: o.id,
                title: o.curriculum,
                masterCurriculumNumber: o.masterCurriculumNumber,
            };
        });
        curricula = sortBy(curricula, ["title"]);
        setCurriculumOptions(curricula);
        setApiLoading(false);
    }, [dropdownOptions]);

    const buildOutLanguageModalityDropdowns = (selectedOption) => {
        const selectedCurriculum = selectedOption || formValues.curriculum;
        const subOptions = dropdownOptions.find(
            (o) => o.curriculum === selectedCurriculum
        );

        let languages = subOptions.language.map((l) => ({
            id: l.id,
            title: l.title,
        }));
        languages = sortBy(languages, ["title"]);

        let modalities = subOptions.deliveryModality.map((dm) => ({
            id: dm.id,
            title: dm.title,
        }));

        let countryOfDeliveries = subOptions.countryOfDeliveries.map((cod) => ({
            id: cod.id,
            title: cod.title,
        }));

        setLanguageOptions(languages);
        setDeliveryModalityOptions(modalities);
        setCountryOfDeliveryOptions(countryOfDeliveries)
    };


    const associatedValue = Object.keys(timezoneMapping).find(
        (key) => timezoneMapping[key] === formValues.timezone
    );

    let selectedValue = timeZoneOptions.find(
        (timezone) => timezone.value === associatedValue
    );

    let changedTimezoneValue = timeZoneOptions.find(
        (timezone) => timezone.value === timeZoneDropdownValue.split("(")[0].trim()
    );

    const furthestDateForward = (startDate) => {
      const updatedDate = new Date(startDate);
      updatedDate.setDate(updatedDate.getDate() + parseInt(maxDuration));
      return updatedDate.toISOString();
    };
  
    const furthestDateBack = (startDate) => {
      const updatedDate = new Date(startDate);
      updatedDate.setDate(updatedDate.getDate() - parseInt(maxDuration));
      return updatedDate.toISOString(); 
    };

    // useEffect to switch on curriculum change to new dropdown filters for language and delivery modality
    useEffect(() => {
        if (curriculumOptions.length !== 0) {
            if (formValues.curriculum !== null) {
                buildOutLanguageModalityDropdowns();
            }
        }
        // eslint-disable-next-line
    }, [curriculumOptions, formValues]);

    // ** Internal form validation to enable submit button
    useEffect(() => {
        if (!Object.values(formValidation).every((value) => value === true)) {
            return setIsFormValid(false);
        }
        return setIsFormValid(true);
    }, [formValidation]);

    const handleSelectedOption = (name, option) => {
        if (!isFormDirty) {
            setIsFormDirty(true);
        }
        // handle resetting language and deliveryModality if curriculum is changed, reset language adn deliveryModality values
        if (name === "curriculum") {
            buildOutLanguageModalityDropdowns(option);
            setFormValues({
                ...formValues,
                language: null,
                deliveryModality: null,
                countryOfDeliveries: null,
                curriculum: option,
            });
            setFormValidation({
                ...formValidation,
                language: false,
                deliveryModality: false,
                countryOfDeliveries: false,
                curriculum: true,
            });
            // set resets for langauge and deliveryModality selects
            setResetSubDropdowns(true);
            setTimeout(() => {
                setResetSubDropdowns(false);
            });
            return;
        }
        setFormValues({ ...formValues, [`${name}`]: option });
        setFormValidation({ ...formValidation, [`${name}`]: true });

        if (name === "startTime" || name === "endTime") {
            if (name === "startTime") {
                setFormValues({
                    ...formValues,
                    start_at: option,
                });
            } else {
                setFormValues({
                    ...formValues,
                    end_at: option,
                });
            }
        }

        if (name === "timezone") {
            const timezoneKeyValue = timeZoneOptions.find(
                (timezoneOption) => timezoneOption.title === option
            );
            const timeZone = Object.keys(timezoneMapping).find(
                (key) => key === timezoneKeyValue.value
            );
            const updatedTimeZone = timezoneMapping[timeZone];

            setTimeZoneDropdownValue(option);
            setFormValues({
                ...formValues,
                timezone: updatedTimeZone,
            });
        }
    };

    const endDateText = () => {
        return (
            <div>
            <Text weight="bold" letterSpacing="normal" size="medium">
                End Date
            </Text>
                &nbsp;&nbsp;
           <Text weight="normal" fontStyle="italic" letterSpacing="normal" size="x-small">
                Within { maxDuration } days of Start Date
            </Text>
            </div>
        );
    }

    const modalityText = () => {
        return (
            <div>
                <Text weight="bold" letterSpacing="normal" size="medium">
                    Delivery Modality
                </Text>
                &nbsp;&nbsp;
                <Text weight="normal" fontStyle="italic" letterSpacing="normal" size="x-small">
                    For reporting purposes only
                </Text>
            </div>
        );
    }

    // Listens for both onChange and onClick events and passes in to internal validation tracker
    // ** Inner FormDateInput component passes a valid date string back if can parse one else null
    const handleDate = (name, dateString) => {
        if (!isFormDirty) {
            setIsFormDirty(true);
        }

        if (dateString !== null) {
            if (name === "startDate") {
                const date = dateString.split("T")[0];
                setFormValues({ ...formValues, startDate: date });
                setFormValidation({ ...formValidation, [`${name}`]: true });
                return;
            } else if (name === "endDate") {
                const date = dateString.split("T")[0];
                setFormValues({ ...formValues, endDate: date });
                setFormValidation({ ...formValidation, [`${name}`]: true });
                return;
            }
        }
        setFormValidation({ ...formValidation, [`${name}`]: false });
        return;
    };

    const calculateCohortValidity = () => {
        const length = formValues.cohort1.length +
              formValues.cohort2.length +
              formValues.cohort3.length +
              formValues.cohort4.length +
              formValues.cohort5.length +
              formValues.cohort6.length;
        if(length > 0 ) {
            setFormValidation({ ...formValidation, ["cohort1"]: true });
        } else if(formValues.cohort1.length === 1 && length === 1) {
            setFormValidation({ ...formValidation, ["cohort1"]: false });
        } else {
            setFormValidation({ ...formValidation, ["cohort1"]: false });
        }
        return;
    }

    const handleCohort = async (name, value) => {
        // When users click into cohort cells this function fires with
        // undefined.  Just return until they start typing, or clear the data.
        if (value === undefined) {
            setTimeout(() => {
                calculateCohortValidity();
            });
            return;
        }
        if (!isFormDirty) {
            setIsFormDirty(true);
        }
        switch (name) {
            case "cohort1":
                // React using the state hook seems to be weird.
                // The state updates asynchonously, and unless I
                // directly update the formValue, it seems like
                // even async functions that wait for state to update
                // don't quite work right.  This doesn't seem to hurt
                // anything, so I am rolling with it.
                formValues.cohort1 = value;
                setFormValues({ ...formValues, cohort1: value });
                break;
            case "cohort2":
                formValues.cohort2 = value;
                setFormValues({ ...formValues, cohort2: value });
                break;
            case "cohort3":
                formValues.cohort3 = value;
                setFormValues({ ...formValues, cohort3: value });
                break;
            case "cohort4":
                formValues.cohort4 = value;
                setFormValues({ ...formValues, cohort4: value });
                break;
            case "cohort5":
                formValues.cohort5 = value;
                setFormValues({ ...formValues, cohort5: value });
                break;
            default:
                formValues.cohort6 = value;
                setFormValues({ ...formValues, cohort6: value });
        }
        if(formValues.cohort1 == null) {
            formValues.cohort1 = ""
        }
        if(formValues.cohort2 == null) {
            formValues.cohort2 = ""
        }
        if(formValues.cohort3 == null) {
            formValues.cohort3 = ""
        }
        if(formValues.cohort4 == null) {
            formValues.cohort4 = ""
        }
        if(formValues.cohort5 == null) {
            formValues.cohort5 = ""
        }
        if(formValues.cohort6 == null) {
            formValues.cohort6 = ""
        }
        setTimeout(() => {
            calculateCohortValidity();
        });
    }

    const handleResetForm = () => {
        dispatch({
            type: ACTIONS.RESET_PROGRESS_ALERT,
        });
        setFormValidation(initialFormValidation);
        setFormValues(initialFormValues);
        formValues.cohort1 = null;
        setTimeZoneDropdownValue("");
        setIsFormDirty(false);
        setResetForm(true);
        setTimeout(() => {
            setResetForm(false);
        }, 0);
    };

    // Grab POST call callback from custom hook for use in handleFormSubmit
    const { postDataCallback } = useCreateCourse();

    const prepPayload = () => {
        const copiedFormValues = JSON.parse(JSON.stringify(formValues));

        const selectedCurriculumObject = curriculumOptions.find(
            (c) => c.title === copiedFormValues.curriculum
        );

        // TimeZone Calculation
        const generateTime = (date, time, timezone) => {
            const [hours, duration] = time.split(" ");
            let [hour, minute] = hours.split(":");

            if (hour === "12" && duration === "AM") {
                hour = "00";
            } else if (hour === "12" && duration === "PM") {
                hour = "12";
            } else if (duration === "PM" && hours !== "12") {
                hour = +hour + 12;
            }

            const updatedTime = `${hour}:${minute}:00`;

            let updatedDate = date + " " + updatedTime;
            updatedDate = zonedTimeToUtc(updatedDate, timezone).toISOString();
            return updatedDate;
        };

        const updatedStart = generateTime(
            copiedFormValues.startDate,
            copiedFormValues.start_at,
            copiedFormValues.timezone
        );
        const updatedEnd = generateTime(
            copiedFormValues.endDate,
            copiedFormValues.end_at,
            copiedFormValues.timezone
        );

        let selectedTimeZone;
        let updatedTimeZone;
        if (timeZoneDropdownValue === "") {
            selectedTimeZone = Object.keys(timezoneMapping).find(
                (timezone) => timezoneMapping[timezone] === initialFormValues.timezone
            );
            updatedTimeZone = { id: selectedTimeZone };
        } else {
            selectedTimeZone = timeZoneDropdownValue.split("(")[0].trim();
            updatedTimeZone = timeZoneOptions.find((tzOptions) =>
                tzOptions.id.includes(selectedTimeZone)
            );
        }


        const preppedPayload = {
            ...copiedFormValues,
            startDate: updatedStart,
            endDate: updatedEnd,
            timezone: updatedTimeZone.id,
            start_at: null,
            end_at: null,
            correctST: null,
            correctET: null,
            cohort1: null,
            cohort2: null,
            cohort3: null,
            cohort4: null,
            cohort5: null,
            cohort6: null,
            cohorts: null,
        };

        if (process.env.NODE_ENV === "production") {
            preppedPayload.id = selectedCurriculumObject.id;
        }

        preppedPayload.masterCurriculumNumber =
            selectedCurriculumObject.masterCurriculumNumber;

        preppedPayload.language_id = languageOptions.find(
            (l) => l.title === copiedFormValues.language
        ).id;

        preppedPayload.country_of_delivery_id = countryOfDeliveryOptions.find(
            (cod) => cod.title == copiedFormValues.countryOfDeliveries
        ).id;

        let cohorts = [];
        cohorts.push(copiedFormValues.cohort1);
        cohorts.push(copiedFormValues.cohort2);
        cohorts.push(copiedFormValues.cohort3);
        cohorts.push(copiedFormValues.cohort4);
        cohorts.push(copiedFormValues.cohort5);
        cohorts.push(copiedFormValues.cohort6);
        // This should filter out all null and "" values.
        preppedPayload.cohorts = cohorts.filter(n => n);
        return preppedPayload;
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();

        dispatch({
            type: ACTIONS.SET_IS_CALLING_API,
            isCallingApi: true,
        });

        if (!isFormValid) {
            console.error("There was an error validating your form");
            dispatch({
                type: ACTIONS.SET_IS_CALLING_API,
                isCallingApi: false,
            });
            return dispatch({
                type: ACTIONS.SET_PROGRESS_ALERT,
                progressAlert: {
                    variant: PROGRESS_ALERT_VARIANTS.ERROR,
                    message: `There was an error validating your form. Contact admins if this error persists.`,
                },
            });
        }

        let postPayload = prepPayload();

        const { response, error } = await postDataCallback(postPayload);

        // POST API error
        if (error !== null) {
            setTimeout(() => {
                dispatch({
                    type: ACTIONS.SET_IS_CALLING_API,
                    isCallingApi: false,
                });
                console.error(`POST call got following error: ${error}`);
                dispatch({
                    type: ACTIONS.SET_PROGRESS_ALERT,
                    progressAlert: {
                        variant: PROGRESS_ALERT_VARIANTS.ERROR,
                        message: error.message
                            ? error.message
                            : "There was an error validating your form. Contact admins if this error persists.",
                    },
                });
            }, 1000);
        }

        // POST API success
        if (response !== null) {
            setTimeout(() => {
                dispatch({
                    type: ACTIONS.SET_IS_CALLING_API,
                    isCallingApi: false,
                });

                handleResetForm();

                const packageForModal = JSON.parse(JSON.stringify(response));

                // TODO - remove for final release
                if (process.env.NODE_ENV === "development") {
                    const randomSuccessError = !!Math.round(Math.random());
                    packageForModal.title = randomSuccessError
                        ? "Test Title Request Processing..."
                        : "Test Error Creating Canvas Course";
                    packageForModal.message = randomSuccessError
                        ? "Your request is being processed. You'll receive a Canvas notification when the course has been successfully created"
                        : "There was an error with Canvas course creation. Please contact your administrator.";
                    packageForModal.error = !randomSuccessError;
                }
                // TODO - remove above for final release

                dispatch({
                    type: ACTIONS.SHOW_CREATED_COURSE_MODAL,
                    createdCourse: packageForModal,
                });
            }, 1000);
        }
    };

    return (
        <View as="section" padding="none medium medium" position="relative">
            <form
                className={classnames(styles.FormCreateCourseRestart)}
                onSubmit={handleFormSubmit}
            >
                <div id="growth">
                <Grid colSpacing="medium" rowSpacing="small" startAt="medium">
                    <Grid.Row>
                        <Grid.Col width={{ small: 4, medium: 4, large: 4, xLarge: 4 }}>
                            <IasFormSelectInputRestart
                                id="curriculum"
                                name="curriculum"
                                label={"Course"}
                                reset={resetForm}
                                placeholder={"Select Course"}
                                isRequired={true}
                                isDisabled={dropdownDataError}
                                onSelected={handleSelectedOption}
                                isLoading={apiLoading}
                                options={curriculumOptions}
                            />
                        </Grid.Col>
                        <Grid.Col width={{ small: 4, medium: 4, large: 4, xLarge: 4 }}>

                        </Grid.Col>
                    </Grid.Row>
                    <Grid.Row>
                        <Grid.Col width={{ small: 3, medium: 3, large: 2, xLarge:4 }} colSpacing="small">
                            <IasFormDateInputRestart
                                id="startDate"
                                name="startDate"
                                label="Start Date"
                                placeholder="Select Start Date"
                                size="medium"
                                timezone={formValues.timezone}
                                minDate={
                                    formValues.endDate
                                        ? furthestDateBack(formValues.endDate)
                                        : null
                                }
                                maxDate={formValues.endDate}
                                dateErrorMessage={"Start date must before End Date"}
                                isRequired={true}
                                minDateErrorMessage={
                                    "Error: Class duration must be no more than " + maxDuration + " days."
                                }
                                isDisabled={dropdownDataError}
                                reset={resetForm}
                                onDateChange={handleDate}
                                onDateBlur={handleDate}
                                onDateSelected={handleDate}
                                isLoading={apiLoading}
                            />
                        </Grid.Col>
                        <Grid.Col width={{ small: 2, medium: 2, large: 2, xLarge: 2}}  colSpacing="small">
                            <IasFormTimeInput
                                id="start_at"
                                name="startTime"
                                renderLabel="Start Time"
                                size="small"
                                value={formValues.start_at}
                                step={30}
                                reset={resetForm}
                                onChange={(_, value, option) => {
                                    handleSelectedOption("startTime", value);
                                }}
                                dropdownDataError={dropdownDataError}
                                isLoading={apiLoading}
                            />
                        </Grid.Col>
                        <Grid.Col width={{ small: 2, medium: 2, large: 2, xLarge:4 }}  colSpacing="small">
                            <IasFormDateInput
                                id="endDate"
                                name="endDate"
                                label= {endDateText()}
                                placeholder="Select End Date"
                                size="medium"
                                timezone={formValues.timezone}
                                minDate={formValues.startDate}
                                maxDate={
                                    formValues.startDate
                                        ? furthestDateForward(formValues.startDate)
                                        : null
                                }
                                dateErrorMessage={"End date must be after Start Date"}
                                isRequired={true}
                                maxDateErrorMessage={
                                    "Error: Class duration must be no more than " + maxDuration + " days."
                                }
                                isDisabled={dropdownDataError}
                                reset={resetForm}
                                onDateChange={handleDate}
                                onDateBlur={handleDate}
                                onDateSelected={handleDate}
                                isLoading={apiLoading}
                            />
                        </Grid.Col>
                        <Grid.Col  width={{ small: 2, medium: 2, large: 2, xLarge: 2}}  colSpacing="small">
                            <IasFormTimeInput
                                id="end_at"
                                name="endTime"
                                renderLabel="End Time"
                                value={formValues.end_at}
                                reset={resetForm}
                                onChange={(_, value, option) => {
                                    handleSelectedOption("endTime", value);
                                }}
                                dropdownDataError={dropdownDataError}
                                isLoading={apiLoading}
                            />
                        </Grid.Col>
                    </Grid.Row>
                    <Grid.Row>
                        <Grid.Col width={{ small: 4, medium: 4, large: 4, xLarge:4 }}>
                            <IasFormSelectInputRestart
                                id="timezone"
                                name="timezone"
                                label={"Time Zone"}
                                reset={resetForm || resetSubDropdowns}
                                placeholder={"Select Time Zone"}
                                isRequired={true}
                                selected={changedTimezoneValue ? changedTimezoneValue : selectedValue}
                                isDisabled={dropdownDataError}
                                onSelected={handleSelectedOption}
                                isLoading={apiLoading}
                                options={timeZoneOptions}
                            />
                        </Grid.Col>
                        <Grid.Col width={{ small: 4, medium: 4, large: 4, xLarge:4 }}>
                            <IasFormSelectInputRestart
                                id="language"
                                name="language"
                                label={"Language"}
                                reset={resetForm || resetSubDropdowns}
                                placeholder={"Select Language..."}
                                isRequired={true}
                                isDisabled={dropdownDataError || formValues.curriculum === null}
                                onSelected={handleSelectedOption}
                                isLoading={apiLoading}
                                options={languageOptions}
                            />
                        </Grid.Col>
                    </Grid.Row>
                    <Grid.Row>
                        <Grid.Col width={{ small: 4, medium: 4, large: 4, xLarge:4 }}>
                            <IasFormSelectInputRestart
                                id="deliveryModality"
                                name="deliveryModality"
                                label={modalityText()}
                                reset={resetForm || resetSubDropdowns}
                                placeholder={"Select Delivery Modality"}
                                isRequired={true}
                                size="medium"
                                isDisabled={dropdownDataError || formValues.curriculum === null}
                                onSelected={handleSelectedOption}
                                isLoading={apiLoading}
                                options={deliveryModalityOptions}
                            />
                        </Grid.Col>
                        <Grid.Col width={{ small: 4, medium: 4, large: 4, xLarge:4 }}>
                            <IasFormSelectInputRestart
                                id="countryOfDeliveries"
                                name="countryOfDeliveries"
                                label={"Country of Delivery"}
                                reset={resetForm || resetSubDropdowns}
                                placeholder={"Select Country of Delivery"}
                                isRequired={true}
                                size="medium"
                                isDisabled={dropdownDataError || formValues.curriculum === null}
                                onSelected={handleSelectedOption}
                                isLoading={apiLoading}
                                options={countryOfDeliveryOptions}
                            />
                        </Grid.Col>
                    </Grid.Row>
                <p />
                {/*Cohort Text Fields. Must contain at least one, and these will be used to create the sections for the course.*/}
                    <Grid.Row>
                        <Grid.Col width={{ small: 4, medium: 4, large: 8, xLarge:4 }} >
                            <Text weight="bold" lineHeight="double">
                                Cohort IDs
                            </Text>
                            &nbsp;&nbsp;
                            <Text weight="normal" fontStyle="italic" letterSpacing="normal" size="x-small">
                               Each class has at least one Cohort ID (one Cohort ID per Section). 
                               Enter the Cohort ID(s) associated with this class in the field(s) below. 
                               If you do not know the Cohort ID(s) associated with this class, contact your AWS re/Start Program Manager.
                            </Text>
                        </Grid.Col>
                        {/*<Grid.Col width={{ small: 4, medium: 4, large: 4, xLarge:4 }}>*/}

                        {/*</Grid.Col>*/}
                    </Grid.Row>
                    <Grid.Row  rowSpacing="none">
                        <Grid.Col width={{ small: 4, medium: 4, large: 4, xLarge:4 }} colSpacing="medium">
                            <IasFormTextInput
                                id="cohort1Txt"
                                name="cohort1Txt"
                                value={formValues.cohort1}
                                reset={resetForm}
                                size="medium"
                                renderLabel="&nbsp;"
                                onChange={(event, value) => { handleCohort("cohort1", value) }}
                            />
                        </Grid.Col>
                        <Grid.Col width={{ small: 4, medium: 4, large: 4, xLarge:4 }} colSpacing="medium">
                            <IasFormTextInput
                                id="cohort2"
                                name="cohort2"
                                value={formValues.cohort2}
                                reset={resetForm}
                                size="medium"
                                renderLabel="&nbsp;"
                                onChange={(event, value) => { handleCohort("cohort2", value) }}
                            />
                        </Grid.Col>
                    </Grid.Row>
                    <Grid.Row>
                        <Grid.Col width={{ small: 4, medium: 4, large: 4, xLarge:4 }} colSpacing="medium">
                            <IasFormTextInput
                                id="cohort3"
                                name="cohort3"
                                value={formValues.cohort3}
                                reset={resetForm}
                                size="medium"
                                renderLabel="&nbsp;"
                                onChange={(event, value) => { handleCohort("cohort3", value) }}
                            />
                        </Grid.Col>
                        <Grid.Col width={{ small: 4, medium: 4, large: 4, xLarge:4 }} colSpacing="medium">
                            <IasFormTextInput
                                id="cohort4"
                                name="cohort4"
                                value={formValues.cohort4}
                                reset={resetForm}
                                size="medium"
                                renderLabel="&nbsp;"
                                onChange={(event, value) => { handleCohort("cohort4", value) }}
                            />
                        </Grid.Col>
                    </Grid.Row>
                    <Grid.Row>
                        <Grid.Col width={{ small: 2, medium: 2, large: 4, xLarge:4 }} colSpacing="medium">
                            <IasFormTextInput
                                id="cohort5"
                                name="cohort5"
                                value={formValues.cohort5}
                                reset={resetForm}
                                size="medium"
                                renderLabel="&nbsp;"
                                onChange={(event, value) => { handleCohort("cohort5", value) }}
                            />
                        </Grid.Col>
                        <Grid.Col width={{ small: 4, medium: 4, large: 4, xLarge:4 }} colSpacing="medium">
                            <IasFormTextInput
                                id="cohort6"
                                name="cohort6"
                                value={formValues.cohort6}
                                reset={resetForm}
                                size="medium"
                                renderLabel="&nbsp;"
                                onChange={(event, value) => { handleCohort("cohort6", value) }}
                            />
                        </Grid.Col>
                    </Grid.Row>
                    <Grid.Row>
                        <Grid.Col width={{ small: 4, medium: 4, large: 4, xLarge:4 }} colSpacing="small">

                        </Grid.Col>
                        <Grid.Col width={{ small: 4, medium: 4, large: 4, xLarge:4 }} colSpacing="small">
                            <IasFormActions
                                isLoading={apiLoading}
                                onReset={handleResetForm}
                                disableReset={!isFormDirty}
                                disableSubmit={!isFormValid}
                            />
                        </Grid.Col>
                    </Grid.Row>
                </Grid>
                </div>
            </form>
        </View>
    );
};

export default IasFormCreateCourseRestart;
